import Grid from "@mui/material/Grid2";
import { FormGroup } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import FormField from "components/cck/FormField";
import TimeUnitSelect from "view/calculationmethods/components/select/TimeUnitSelect";
import { Fragment, ReactNode, useEffect } from "react";
import { TimeUnit, AgreementTypeEnum } from "__generated__/graphql";
import NumberInput from "components/cck/NumberInput";
import type { CalculationMethodFormByTypeProps } from "@schemas/calculationmethods/schema";
import { usePermission } from "components/utils/Permission";

type FormValues = {
	agreements: {
		type?: AgreementTypeEnum;
		calculationMethod: {
			timeUnit?: TimeUnit;
			price?: number;
			minimumTimeUnitsPayable?: number;
		}
	}[];
}

type FormField = {
	id: string;
	render: () => ReactNode;
}

const TimeUnitCalculationMethodForm = ({ hiddenFields, index, currency }: CalculationMethodFormByTypeProps) => {
	const { isTankmatch } = usePermission();

	const { control, setValue, formState: { errors } } = useFormContext<FormValues>();

	const timeUnit = useWatch({ control, name: `agreements.${index}.calculationMethod.timeUnit` });

	useEffect(() => {
		if (isTankmatch && timeUnit !== TimeUnit.DayProRata) {
			setValue(`agreements.${index}.calculationMethod.timeUnit`, TimeUnit.DayProRata);
		}
	}, [ isTankmatch, timeUnit, index, setValue ]);

	const TimeUnitCalculationMethodFormFields: FormField[] = [
		{
			id: "calculationMethod.timeUnit",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.timeUnit`}>
						<FormGroup label={intl.formatMessage({ id: "TIME_UNIT" })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.timeUnit`}
								control={control}
								render={({ field }) => (
									<TimeUnitSelect
										options={isTankmatch ? [
											TimeUnit.DayProRata,
										] : undefined}
										{...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.price",
			render: () => timeUnit && (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.price`}>
						<FormGroup label={intl.formatMessage({ id: `PRICE_PER_${timeUnit}` })} overflow="hidden" required>
							<Controller
								name={`agreements.${index}.calculationMethod.price`}
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<NumberInput
										{...field}
										addonBefore={currency.symbol} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
		{
			id: "calculationMethod.minimumTimeUnitsPayable",
			render: () => (
				<Grid size={4}>
					<FormField errors={errors} name={`agreements.${index}.calculationMethod.minimumTimeUnitsPayable`}>
						<FormGroup label={intl.formatMessage({ id: "MINIMUM_TIME_UNITS_PAYABLE" })} overflow="hidden">
							<Controller
								name={`agreements.${index}.calculationMethod.minimumTimeUnitsPayable`}
								control={control}
								render={({ field }) => (
									<NumberInput {...field} />
								)} />
						</FormGroup>
					</FormField>
				</Grid>
			),
		},
	];

	return (
		<>
			{TimeUnitCalculationMethodFormFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};

export default TimeUnitCalculationMethodForm;
